import { validateSubAssemblyTypeUniqueness } from "./landingGearAttachUtils";

export const LANDING_GEAR_SUB_ASSEMBLY_REQUIRED_MESSAGE =
  "Attach at least one sub-assembly when landing gear has sub-assembly.";

export const LANDING_GEAR_LAST_SUB_ASSEMBLY_DETACH_MESSAGE =
  "Cannot detach the last sub-assembly. At least one must remain attached.";

export const LANDING_GEAR_SAVE_BEFORE_DETACH_MESSAGE =
  "Save the newly attached sub-assembly before detaching this one.";

type SubAssemblyPersistence = { isPersisted?: boolean };

/**
 * Returns a disable/tooltip reason when a persisted sub-assembly cannot be detached.
 * Create-mode and unsaved pending items are never blocked by this rule.
 */
export function getPersistedSubAssemblyDetachBlockReason(
  pendingSubAssemblies: SubAssemblyPersistence[],
  options: { isCreate: boolean; isPersisted?: boolean }
): string | null {
  if (options.isCreate || !options.isPersisted) {
    return null;
  }

  const persistedCount = pendingSubAssemblies.filter((item) => item.isPersisted).length;
  if (persistedCount > 1) {
    return null;
  }

  const hasUnsavedAttachments = pendingSubAssemblies.some((item) => !item.isPersisted);
  return hasUnsavedAttachments
    ? LANDING_GEAR_SAVE_BEFORE_DETACH_MESSAGE
    : LANDING_GEAR_LAST_SUB_ASSEMBLY_DETACH_MESSAGE;
}

export type ComponentFormValidationInput = {
  serialNumber: string;
  manufacturer?: string;
  model?: string;
  isAttached: boolean;
  offWingType: string;
  shouldShowReason: boolean;
  shopVisitReasonId?: string;
  isLandingGear: boolean;
  /** Checkbox state — when true, at least one sub-assembly must be attached. */
  hasSubAssemblyChecked: boolean;
  /** Derived flag used to skip off-wing fields when sub-assemblies are in play. */
  hasSubAssembly: boolean;
  pendingSubAssemblyCount: number;
  pendingSubAssemblyLandingGearTypes?: string[];
  pendingSubAssemblyTypes?: string[];
};

export function validateComponentForm(
  input: ComponentFormValidationInput
): string[] {
  const errors: string[] = [];

  if (!input.serialNumber.trim()) {
    errors.push("Serial Number is required.");
  }

  if (!input.manufacturer?.trim()) {
    errors.push("Manufacturer is required.");
  }

  if (!input.model?.trim()) {
    errors.push("Model is required.");
  }

  const showOffWingDetails =
    !input.isAttached &&
    !(
      input.isLandingGear &&
      (input.hasSubAssembly || input.pendingSubAssemblyCount > 0)
    );

  if (showOffWingDetails && !input.offWingType.trim()) {
    errors.push("OffWing Type is required.");
  }

  if (
    showOffWingDetails &&
    input.offWingType.trim() === "In Shop" &&
    !input.shopVisitReasonId?.trim()
  ) {
    errors.push("Inshop Reason is required when OffWing Type is In Shop.");
  }

  if (
    input.isLandingGear &&
    input.hasSubAssemblyChecked &&
    input.pendingSubAssemblyCount === 0
  ) {
    errors.push(LANDING_GEAR_SUB_ASSEMBLY_REQUIRED_MESSAGE);
  }

  if (input.isLandingGear && input.pendingSubAssemblyLandingGearTypes?.length) {
    const normalizedTypes = Array.from(
      new Set(
        input.pendingSubAssemblyLandingGearTypes
          .map((type) => type.trim().toUpperCase())
          .filter(Boolean)
      )
    );

    if (normalizedTypes.length > 1) {
      errors.push(
        "Sub-assemblies must all belong to the same landing gear type (RMLG, LMLG, or NLG)."
      );
    }
  }

  if (input.isLandingGear && input.pendingSubAssemblyTypes?.length) {
    const duplicateTypeError = validateSubAssemblyTypeUniqueness(
      input.pendingSubAssemblyTypes.map((subAssemblyType) => ({ subAssemblyType }))
    );

    if (duplicateTypeError) {
      errors.push(duplicateTypeError);
    }
  }

  return errors;
}
